import { useEffect } from 'react';

import { DragControlsEvent, DragControlsImpl } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

import { ConnectControlsHookOptions, useConnectControls, useDollhouse } from '../../hooks/index.js';
export {
  type DragControlsEventHandler,
  type DragControlsEventOptions,
  type DragControlsEvents,
  type DragControlsModifier,
  DragControls,
  DragControlsEvent,
  DragControlsImpl,
  createDragControlsEvent,
} from '@spazious/dollhouse-three';

export interface DragControlsHookOptions extends ConnectControlsHookOptions {
  onDrag: (evt: DragControlsEvent) => void;
}

export const useDragControls = ({ onDrag }: DragControlsHookOptions) => {
  const controls = useDollhouse(({ controls }) => controls);

  const dragControls = useConnectControls<typeof DragControlsImpl>(DragControlsImpl);

  useEffect(() => {
    return () => {
      if (controls) {
        (controls as any).enabled = true;
      }
    };
  }, [controls]);

  const handleDrag = useEventCallback((evt: DragControlsEvent) => {
    onDrag?.(evt);
    if (evt.phase === 'start') {
      if (controls) {
        (controls as any).enabled = false;
      }
    } else if (evt.phase === 'end') {
      if (controls) {
        (controls as any).enabled = true;
      }
    }
  });

  useEffect(() => {
    dragControls.addEventListener('dragstart', handleDrag);
    dragControls.addEventListener('dragend', handleDrag);
    dragControls.addEventListener('drag', handleDrag);
    dragControls.addEventListener('dragcancel', handleDrag);

    return () => {
      dragControls.removeEventListener('dragstart', handleDrag);
      dragControls.removeEventListener('dragend', handleDrag);
      dragControls.removeEventListener('drag', handleDrag);
      dragControls.removeEventListener('dragcancel', handleDrag);
    };
  }, [dragControls, handleDrag]);

  return dragControls;
};
